/**
 * Responsive Video Loader
 * Loads only the appropriate video (desktop or mobile) based on viewport size
 * Prevents both versions from loading simultaneously, improving page performance
 */
(function() {
    'use strict';

    const MOBILE_BREAKPOINT = 551;
    let currentViewport = window.innerWidth > MOBILE_BREAKPOINT ? 'desktop' : 'mobile';
    let resizeTimeout = null;

    /**
     * Get the appropriate video element based on viewport
     */
    function getVisibleVideo(container) {
        const videos = container.querySelectorAll('video[data-autoplay-video]');
        if (videos.length === 0) return null;
        if (videos.length === 1) return videos[0];

        // Multiple videos - find the one for current viewport
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
        
        for (let video of videos) {
            const hasMobileClass = video.classList.contains('slide-mobile');
            const hasPcClass = video.classList.contains('slide-pc');
            
            if (isMobile && hasMobileClass) return video;
            if (!isMobile && hasPcClass) return video;
            if (!hasMobileClass && !hasPcClass) return video; // Default/only video
        }

        // Fallback to first video
        return videos[0];
    }

    /**
     * Load video source and start playing
     */
    function loadAndPlayVideo(video) {
        if (!video || video.dataset.loaded === 'true') return;

        const src = video.dataset.src;
        if (!src) return;

        // Create and add source
        const source = document.createElement('source');
        source.type = 'video/mp4';
        source.src = src;
        video.appendChild(source);

        // Mark as loaded
        video.dataset.loaded = 'true';

        // Load and play
        video.load();
        
        video.addEventListener('loadeddata', function() {
            video.play().catch(function(e) {
                // Autoplay might be blocked - that's okay
                console.debug('Video autoplay blocked:', e);
            });
        }, { once: true });
    }

    /**
     * Pause and unload video (for viewport changes)
     */
    function unloadVideo(video) {
        if (!video || video.dataset.loaded !== 'true') return;

        video.pause();
        // Remove source to stop downloading
        const source = video.querySelector('source');
        if (source) {
            source.remove();
        }
        video.removeAttribute('src');
        video.load(); // Reset
        video.dataset.loaded = 'false';
    }

    /**
     * Initialize videos in slideshow containers
     */
    function initSlideshowVideos() {
        const videoContainers = document.querySelectorAll('[data-video-mp4]');
        
        videoContainers.forEach(function(container) {
            const visibleVideo = getVisibleVideo(container);
            if (visibleVideo) {
                // Use IntersectionObserver to only load when visible
                observeVideo(container, visibleVideo);
            }
        });
    }

    /**
     * IntersectionObserver for lazy loading slideshow videos
     */
    let videoIntersectionObserver = null;

    function observeVideo(container, video) {
        if (!videoIntersectionObserver) {
            videoIntersectionObserver = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting) {
                        const container = entry.target;
                        const visibleVideo = getVisibleVideo(container);
                        if (visibleVideo) {
                            loadAndPlayVideo(visibleVideo);
                        }
                    } else {
                        // Optional: pause video when out of view to save resources
                        const container = entry.target;
                        const videos = container.querySelectorAll('video[data-autoplay-video]');
                        videos.forEach(function(v) {
                            if (!v.paused) {
                                v.pause();
                            }
                        });
                    }
                });
            }, {
                rootMargin: '100px',
                threshold: 0.1
            });
        }

        videoIntersectionObserver.observe(container);
    }

    /**
     * Handle viewport resize - switch videos if needed
     */
    function handleResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function() {
            const newViewport = window.innerWidth > MOBILE_BREAKPOINT ? 'desktop' : 'mobile';
            
            if (newViewport !== currentViewport) {
                currentViewport = newViewport;
                
                // Re-evaluate which video should be playing
                const videoContainers = document.querySelectorAll('[data-video-mp4]');
                videoContainers.forEach(function(container) {
                    const videos = container.querySelectorAll('video[data-autoplay-video]');
                    
                    videos.forEach(function(video) {
                        const shouldBeVisible = video === getVisibleVideo(container);
                        
                        if (shouldBeVisible && video.dataset.loaded !== 'true') {
                            // Check if container is visible
                            const rect = container.getBoundingClientRect();
                            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
                            if (isVisible) {
                                loadAndPlayVideo(video);
                            }
                        } else if (!shouldBeVisible && video.dataset.loaded === 'true') {
                            unloadVideo(video);
                        }
                    });
                });
            }
        }, 250);
    }

    /**
     * Initialize on DOM ready
     */
    function init() {
        initSlideshowVideos();
        window.addEventListener('resize', handleResize);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Handle Shopify theme editor
    document.addEventListener('shopify:section:load', function(e) {
        if (e.target.querySelector('[data-video-mp4]')) {
            initSlideshowVideos();
        }
    });
})();
